import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { ChartCard } from '../components/ChartCard'

/**
 * One chart system for the whole admin panel (Task 9's `dataviz` brief):
 * every chart shares the same faint grid, axis typography, tooltip
 * treatment and tabular figures, and draws exclusively from `theme.ts`'s
 * CHU palette tokens — `brand` (Fern Green) for the primary/"spend" series,
 * `ring` (Gumleaf Teal) for a secondary series on the same chart, never an
 * invented hue. Class names below (`chu-chart-series-*`) exist purely as a
 * stable test/inspection hook tying each rendered series back to its token.
 */
const SERIES_BRAND = { token: 'brand', className: 'chu-chart-series-brand' } as const
const SERIES_RING = { token: 'ring', className: 'chu-chart-series-ring' } as const

const TABULAR: CSSProperties = { fontVariantNumeric: 'tabular-nums' }

/** `days` mirrors the backend's whitelist in `routes/dashboards.ts`
 *  (`ALLOWED_DAYS`) exactly — any other value is a 400 there. */
// Widened to match the backend's ALLOWED_DAYS. 7 is the week an operator
// actually asks about; 180 is currently longer than the platform has
// existed and simply returns what exists, which is fine — a time series
// shows its own start.
const DAY_OPTIONS = [7, 14, 30, 90, 180] as const
type DayRange = (typeof DAY_OPTIONS)[number]

type TabKey = 'cost' | 'usage' | 'adoption' | 'topics' | 'budget'

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'cost', label: 'Cost' },
  { key: 'usage', label: 'Usage' },
  { key: 'adoption', label: 'Adoption' },
  { key: 'topics', label: 'Topics' },
  { key: 'budget', label: 'Budget' },
]

/** `GET /api/dash/cost` (`backend/src/routes/dashboards.ts`). Postgres
 *  `numeric`/`bigint` columns (spend, calls, tokens) come back as strings
 *  over JSON — see `num()` below, applied at every render site rather than
 *  trusting the wire shape. */
interface CostByUser {
  user: string
  spend: number | string
  calls: number | string
  tokens: number | string
}
interface CostByModel {
  model: string
  spend: number | string
  calls: number | string
}
interface CostDaily {
  day: string
  spend: number | string
}
interface CostData {
  costPer1k?: CostPer1k[]
  byUser: CostByUser[]
  byModel: CostByModel[]
  daily: CostDaily[]
}

/** `GET /api/dash/usage`. */
interface UsageActiveUsersDaily {
  day: string
  active_users: number | string
  new_users: number | string
}
interface UsageTokensDaily {
  day: string
  tokens: number | string
}
interface UsageData {
  activeUsersDaily: UsageActiveUsersDaily[]
  tokensDaily: UsageTokensDaily[]
  featureMix: Array<{ feature: string; count: number | string }>
}

/** `GET /api/dash/adoption`. */
interface AdoptionNewUsers {
  day: string
  new_users: number | string
}
interface AdoptionByDepartment {
  department: string
  users: number | string
}
/** Onboarded vs ever-active vs active-this-window, per department. The GAP
 *  between the columns is the finding. */
interface DepartmentCoverage {
  department: string
  onboarded: number | string
  ever_active: number | string
  active_in_window: number | string
}
interface AdoptionData {
  newUsers: AdoptionNewUsers[]
  byDepartment: AdoptionByDepartment[]
  topicTrend: Array<{ day: string; topic: string; count: number | string }>
  coverage?: DepartmentCoverage[]
}

/** `GET /api/dash/cost`'s cost-per-1,000-tokens cut. */
interface CostPer1k {
  model: string
  spend: number | string
  tokens: number | string
  cost_per_1k: number | string
}

/** `GET /api/dash/topics`. Titles only, always. */
interface TopicsData {
  mix: Array<{ day: string; topic: string; count: number | string }>
  byDepartment: Array<{ department: string; topic: string; count: number | string }>
  byModel: Array<{ topic: string; model: string; count: number | string }>
  top3Share: number
  privacyNote: string
}

/** `GET /api/dash/budget`. `burn` and `monthToDate`/`projectedMonthEnd` read
 *  `LiteLLM_SpendLogs` live (not the nightly warehouse); `byDepartment` reads
 *  `daily_user_model_spend` joined to `user_directory`, since department only
 *  exists in the warehouse. `hideCosts` only ever changes `topMovers`'
 *  `metric` from spend to tokens; the platform/department aggregates below it
 *  show regardless, matching Overview's own precedent for what hiding costs
 *  means. */
interface BudgetBurnDay {
  day: string
  chat_spend: number | string
  developer_spend: number | string
}
interface BudgetMover {
  email: string
  metric: 'spend' | 'tokens'
  thisWeek: number | string
  priorWeek: number | string
  delta: number | string
}
interface BudgetByDepartment {
  department: string
  spend: number | string
  people: number | string
}
interface BudgetData {
  hideCosts: boolean
  burn: BudgetBurnDay[]
  monthToDate: number | string
  projectedMonthEnd: number | string
  daysElapsed: number | string
  daysInMonth: number | string
  topMovers: BudgetMover[]
  byDepartment: BudgetByDepartment[]
}

/** Postgres `numeric`/`bigint` results travel as strings over JSON; every
 *  chart value is coerced through here rather than trusted as a number. */
function num(value: number | string | undefined | null): number {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

function money(value: number | string): string {
  return `$${num(value).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function shortDay(day: string): string {
  const date = new Date(day)
  return Number.isNaN(date.getTime())
    ? day
    : date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'Australia/Sydney' })
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return `${err.message} (HTTP ${err.status})`
  }
  return err instanceof Error ? err.message : String(err)
}

/** Recharts' `<Tooltip formatter>`/`labelFormatter` types accept a
 *  `ValueType | undefined` / `ReactNode`, wider than the `number/string`
 *  these charts actually pass — these wrappers narrow at the call site
 *  instead of loosening every chart's own value typing. */
function tooltipMoney(value: unknown): string {
  return money(Number(value ?? 0))
}
function tooltipNumber(value: unknown): string {
  return Number(value ?? 0).toLocaleString('en-AU')
}
function tooltipDayLabel(label: unknown): string {
  return typeof label === 'string' ? shortDay(label) : String(label ?? '')
}

function axisTick(tokens: ThemeTokens) {
  return { fill: tokens.textSecondary, fontSize: 11, fontVariantNumeric: 'tabular-nums' as const }
}

/** Shared `<Tooltip>` props: readable on both themes, tabular figures, no
 *  drop-shadow chart-junk beyond the token-driven card shadow already used
 *  elsewhere in the app. */
function tooltipProps(tokens: ThemeTokens) {
  return {
    contentStyle: {
      background: tokens.surfaceRaised,
      border: `1px solid ${tokens.border}`,
      borderRadius: '0.6rem',
      fontSize: '0.78rem',
      color: tokens.textPrimary,
      fontVariantNumeric: 'tabular-nums' as const,
      boxShadow: tokens.shadow,
      padding: '0.5rem 0.7rem',
    },
    labelStyle: { color: tokens.textSecondary, marginBottom: '0.2rem', fontWeight: 600 },
    cursor: { fill: tokens.surfaceRaised },
  }
}

/** A faint horizontal-only grid — the dataviz brief's "faint grid, no
 *  chart-junk": no vertical gridlines, hairline colour from `border`. */
function Grid({ tokens }: { tokens: ThemeTokens }) {
  return <CartesianGrid stroke={tokens.border} strokeDasharray="3 3" vertical={false} />
}

/** Renders nothing for every point except the last, where it draws an
 *  emphasized dot in the series colour with a surface-coloured ring — the
 *  dataviz brief's "an emphasized endpoint" on trend lines. */
function endpointDot(tokens: ThemeTokens, color: string, length: number) {
  return function Dot(props: { cx?: number; cy?: number; index?: number }) {
    const { cx, cy, index } = props
    if (index !== length - 1 || cx == null || cy == null) {
      return <g key={`dot-${index}`} />
    }
    return <circle key={`dot-${index}`} cx={cx} cy={cy} r={4.5} fill={color} stroke={tokens.surface} strokeWidth={2} />
  }
}

function EmptyChart({ tokens, label }: { tokens: ThemeTokens; label: string }) {
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ color: tokens.textSecondary, fontSize: '0.82rem' }}>{label}</span>
    </div>
  )
}

function SpendByUserChart({ tokens, data }: { tokens: ThemeTokens; data: CostByUser[] }) {
  if (data.length === 0) {
    return <EmptyChart tokens={tokens} label="No spend in this window." />
  }
  const rows = data.map((row) => ({ user: row.user, spend: num(row.spend) }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 12, top: 4, bottom: 4 }}>
        <Grid tokens={tokens} />
        <XAxis type="number" tick={axisTick(tokens)} stroke={tokens.border} tickFormatter={(v) => money(v)} />
        <YAxis
          type="category"
          dataKey="user"
          width={140}
          tick={axisTick(tokens)}
          stroke={tokens.border}
          tickFormatter={(v: string) => (v.length > 18 ? `${v.slice(0, 17)}…` : v)}
        />
        <Tooltip {...tooltipProps(tokens)} formatter={tooltipMoney} />
        <Bar
          dataKey="spend"
          name="Spend"
          fill={tokens.brand}
          className={SERIES_BRAND.className}
          radius={[0, 4, 4, 0]}
          maxBarSize={22}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}

function SpendByModelChart({ tokens, data }: { tokens: ThemeTokens; data: CostByModel[] }) {
  if (data.length === 0) {
    return <EmptyChart tokens={tokens} label="No spend in this window." />
  }
  const rows = data.map((row) => ({ model: row.model, spend: num(row.spend) }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 12, top: 4, bottom: 4 }}>
        <Grid tokens={tokens} />
        <XAxis type="number" tick={axisTick(tokens)} stroke={tokens.border} tickFormatter={(v) => money(v)} />
        <YAxis type="category" dataKey="model" width={130} tick={axisTick(tokens)} stroke={tokens.border} />
        <Tooltip {...tooltipProps(tokens)} formatter={tooltipMoney} />
        <Bar
          dataKey="spend"
          name="Spend"
          fill={tokens.brand}
          className={SERIES_BRAND.className}
          radius={[0, 4, 4, 0]}
          maxBarSize={22}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}

function DailySpendTrendChart({ tokens, data }: { tokens: ThemeTokens; data: CostDaily[] }) {
  if (data.length === 0) {
    return <EmptyChart tokens={tokens} label="No spend in this window." />
  }
  const rows = data.map((row) => ({ day: row.day, spend: num(row.spend) }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={rows} margin={{ left: 4, right: 16, top: 8, bottom: 4 }}>
        <Grid tokens={tokens} />
        <XAxis dataKey="day" tick={axisTick(tokens)} stroke={tokens.border} tickFormatter={shortDay} minTickGap={24} />
        <YAxis tick={axisTick(tokens)} stroke={tokens.border} tickFormatter={(v) => money(v)} width={64} />
        <Tooltip {...tooltipProps(tokens)} labelFormatter={tooltipDayLabel} formatter={tooltipMoney} />
        <Line
          type="monotone"
          dataKey="spend"
          name="Spend"
          stroke={tokens.brand}
          className={SERIES_BRAND.className}
          strokeWidth={2}
          dot={endpointDot(tokens, tokens.brand, rows.length)}
          activeDot={{ r: 4, fill: tokens.brand }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

function ActiveUsersTrendChart({ tokens, data }: { tokens: ThemeTokens; data: UsageActiveUsersDaily[] }) {
  if (data.length === 0) {
    return <EmptyChart tokens={tokens} label="No usage in this window." />
  }
  const rows = data.map((row) => ({ day: row.day, active: num(row.active_users), fresh: num(row.new_users) }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={rows} margin={{ left: 4, right: 16, top: 8, bottom: 4 }}>
        <Grid tokens={tokens} />
        <XAxis dataKey="day" tick={axisTick(tokens)} stroke={tokens.border} tickFormatter={shortDay} minTickGap={24} />
        <YAxis tick={axisTick(tokens)} stroke={tokens.border} width={40} allowDecimals={false} />
        <Tooltip {...tooltipProps(tokens)} labelFormatter={tooltipDayLabel} />
        <Line
          type="monotone"
          dataKey="active"
          name="Active users"
          stroke={tokens.brand}
          className={SERIES_BRAND.className}
          strokeWidth={2}
          dot={endpointDot(tokens, tokens.brand, rows.length)}
          activeDot={{ r: 4, fill: tokens.brand }}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="fresh"
          name="New users"
          stroke={tokens.ring}
          className={SERIES_RING.className}
          strokeWidth={2}
          dot={endpointDot(tokens, tokens.ring, rows.length)}
          activeDot={{ r: 4, fill: tokens.ring }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

function TokensPerDayChart({ tokens, data }: { tokens: ThemeTokens; data: UsageTokensDaily[] }) {
  if (data.length === 0) {
    return <EmptyChart tokens={tokens} label="No token usage in this window." />
  }
  const rows = data.map((row) => ({ day: row.day, tokens: num(row.tokens) }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} margin={{ left: 4, right: 16, top: 8, bottom: 4 }}>
        <Grid tokens={tokens} />
        <XAxis dataKey="day" tick={axisTick(tokens)} stroke={tokens.border} tickFormatter={shortDay} minTickGap={24} />
        <YAxis
          tick={axisTick(tokens)}
          stroke={tokens.border}
          width={56}
          tickFormatter={(v: number) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))}
        />
        <Tooltip {...tooltipProps(tokens)} labelFormatter={tooltipDayLabel} formatter={tooltipNumber} />
        <Bar dataKey="tokens" name="Tokens" fill={tokens.brand} className={SERIES_BRAND.className} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  )
}

function NewUsersChart({ tokens, data }: { tokens: ThemeTokens; data: AdoptionNewUsers[] }) {
  if (data.length === 0) {
    return <EmptyChart tokens={tokens} label="No new users in this window." />
  }
  const rows = data.map((row) => ({ day: row.day, fresh: num(row.new_users) }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} margin={{ left: 4, right: 16, top: 8, bottom: 4 }}>
        <Grid tokens={tokens} />
        <XAxis dataKey="day" tick={axisTick(tokens)} stroke={tokens.border} tickFormatter={shortDay} minTickGap={24} />
        <YAxis tick={axisTick(tokens)} stroke={tokens.border} width={32} allowDecimals={false} />
        <Tooltip {...tooltipProps(tokens)} labelFormatter={tooltipDayLabel} />
        <Bar dataKey="fresh" name="New users" fill={tokens.brand} className={SERIES_BRAND.className} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  )
}

function ByDepartmentChart({ tokens, data }: { tokens: ThemeTokens; data: AdoptionByDepartment[] }) {
  if (data.length === 0) {
    return <EmptyChart tokens={tokens} label="No department data in this window." />
  }
  const rows = data.map((row) => ({ department: row.department, users: num(row.users) }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 12, top: 4, bottom: 4 }}>
        <Grid tokens={tokens} />
        <XAxis type="number" tick={axisTick(tokens)} stroke={tokens.border} allowDecimals={false} />
        <YAxis type="category" dataKey="department" width={110} tick={axisTick(tokens)} stroke={tokens.border} />
        <Tooltip {...tooltipProps(tokens)} />
        <Bar dataKey="users" name="Users" fill={tokens.brand} className={SERIES_BRAND.className} radius={[0, 4, 4, 0]} maxBarSize={22} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  )
}

function BurnOverTimeChart({ tokens, data }: { tokens: ThemeTokens; data: BudgetBurnDay[] }) {
  if (data.length === 0) {
    return <EmptyChart tokens={tokens} label="No spend in this window." />
  }
  const rows = data.map((row) => ({ day: row.day, chat: num(row.chat_spend), developer: num(row.developer_spend) }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={rows} margin={{ left: 4, right: 16, top: 8, bottom: 4 }}>
        <Grid tokens={tokens} />
        <XAxis dataKey="day" tick={axisTick(tokens)} stroke={tokens.border} tickFormatter={shortDay} minTickGap={24} />
        <YAxis tick={axisTick(tokens)} stroke={tokens.border} tickFormatter={(v) => money(v)} width={64} />
        <Tooltip {...tooltipProps(tokens)} labelFormatter={tooltipDayLabel} formatter={tooltipMoney} />
        <Line
          type="monotone"
          dataKey="chat"
          name="Chat"
          stroke={tokens.brand}
          className={SERIES_BRAND.className}
          strokeWidth={2}
          dot={endpointDot(tokens, tokens.brand, rows.length)}
          activeDot={{ r: 4, fill: tokens.brand }}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="developer"
          name="Developer"
          stroke={tokens.ring}
          className={SERIES_RING.className}
          strokeWidth={2}
          dot={endpointDot(tokens, tokens.ring, rows.length)}
          activeDot={{ r: 4, fill: tokens.ring }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

function SpendByDepartmentChart({ tokens, data }: { tokens: ThemeTokens; data: BudgetByDepartment[] }) {
  if (data.length === 0) {
    return <EmptyChart tokens={tokens} label="No department spend in this window." />
  }
  const rows = data.map((row) => ({ department: row.department, spend: num(row.spend) }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 12, top: 4, bottom: 4 }}>
        <Grid tokens={tokens} />
        <XAxis type="number" tick={axisTick(tokens)} stroke={tokens.border} tickFormatter={(v: number) => money(v)} />
        <YAxis type="category" dataKey="department" width={110} tick={axisTick(tokens)} stroke={tokens.border} />
        <Tooltip {...tooltipProps(tokens)} formatter={tooltipMoney} />
        <Bar dataKey="spend" name="Spend" fill={tokens.brand} className={SERIES_BRAND.className} radius={[0, 4, 4, 0]} maxBarSize={22} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  )
}

/** Biggest week-over-week movers. `metric` says honestly whether the ranking
 *  is spend or tokens (`hideCosts` switches it), so the header and the
 *  values never disagree about what is being counted. */
function TopMoversTable({ tokens, data }: { tokens: ThemeTokens; data: BudgetMover[] }) {
  if (data.length === 0) {
    return <p style={{ color: tokens.textSecondary, fontSize: '0.86rem' }}>No movement to report in this window.</p>
  }
  const isSpend = data[0]?.metric === 'spend'
  const fmt = (v: number | string) => (isSpend ? money(num(v)) : num(v).toLocaleString('en-AU'))
  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%', minWidth: 0 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
        <thead>
          <tr>
            {['Person', isSpend ? 'This week' : 'This week (tokens)', isSpend ? 'Prior week' : 'Prior week (tokens)', 'Change'].map((header, i) => (
              <th
                key={header}
                style={{
                  textAlign: i === 0 ? 'left' : 'right',
                  padding: '0.4rem 0.5rem',
                  borderBottom: `1px solid ${tokens.border}`,
                  color: tokens.textSecondary,
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.email}>
              <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${tokens.border}`, color: tokens.textPrimary }}>
                {row.email}
              </td>
              <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${tokens.border}`, textAlign: 'right', color: tokens.textPrimary, ...TABULAR }}>
                {fmt(row.thisWeek)}
              </td>
              <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${tokens.border}`, textAlign: 'right', color: tokens.textSecondary, ...TABULAR }}>
                {fmt(row.priorWeek)}
              </td>
              <td
                style={{
                  padding: '0.4rem 0.5rem',
                  borderBottom: `1px solid ${tokens.border}`,
                  textAlign: 'right',
                  color: num(row.delta) >= 0 ? tokens.brand : tokens.textSecondary,
                  ...TABULAR,
                }}
              >
                {num(row.delta) >= 0 ? '+' : ''}
                {fmt(row.delta)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Month-to-date spend against a linear projection to month end. No chart
 *  earns its place here more cheaply than two numbers and the day count
 *  behind them. */
function MonthToDatePanel({ tokens, budget }: { tokens: ThemeTokens; budget: BudgetData }) {
  const daysElapsed = num(budget.daysElapsed)
  const daysInMonth = num(budget.daysInMonth)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', height: '100%', justifyContent: 'center' }}>
      <div>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: tokens.textSecondary }}>Spend so far</div>
        <div style={{ fontSize: '1.8rem', fontWeight: 700, color: tokens.textPrimary, ...TABULAR }}>{money(budget.monthToDate)}</div>
      </div>
      <div>
        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: tokens.textSecondary }}>Projected month end</div>
        <div style={{ fontSize: '1.8rem', fontWeight: 700, color: tokens.brand, ...TABULAR }}>{money(budget.projectedMonthEnd)}</div>
      </div>
      <p style={{ margin: 0, fontSize: '0.78rem', color: tokens.textSecondary }}>
        Day {daysElapsed} of {daysInMonth}, projected by a straight-line extrapolation of the month so far. Source:
        LiteLLM_SpendLogs, Sydney calendar month.
      </p>
    </div>
  )
}

/** Feature/tool mix — how usage splits across features (code, artifacts, web
 *  search, tools). Horizontal bars, biggest first. */
function FeatureMixChart({ tokens, data }: { tokens: ThemeTokens; data: Array<{ feature: string; count: number | string }> }) {
  const rows = data.map((r) => ({ feature: r.feature, count: num(r.count) })).sort((a, b) => b.count - a.count)
  if (rows.length === 0) {
    return <EmptyChart tokens={tokens} label="No feature usage in this window." />
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 12, top: 4, bottom: 4 }}>
        <Grid tokens={tokens} />
        <XAxis type="number" tick={axisTick(tokens)} stroke={tokens.border} allowDecimals={false} />
        <YAxis type="category" dataKey="feature" width={130} tick={axisTick(tokens)} stroke={tokens.border} />
        <Tooltip {...tooltipProps(tokens)} />
        <Bar dataKey="count" name="Uses" fill={tokens.brand} className={SERIES_BRAND.className} radius={[0, 4, 4, 0]} maxBarSize={22} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  )
}

/** Topic taxonomy — conversation topics ranked by volume over the window
 *  (aggregated from the daily topic trend; titles-only, no message content). */
function TopicsChart({ tokens, data }: { tokens: ThemeTokens; data: Array<{ day: string; topic: string; count: number | string }> }) {
  const totals = new Map<string, number>()
  for (const r of data) totals.set(r.topic, (totals.get(r.topic) ?? 0) + num(r.count))
  const rows = [...totals.entries()].map(([topic, count]) => ({ topic, count })).sort((a, b) => b.count - a.count).slice(0, 12)
  if (rows.length === 0) {
    return <EmptyChart tokens={tokens} label="No topic data in this window." />
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 12, top: 4, bottom: 4 }}>
        <Grid tokens={tokens} />
        <XAxis type="number" tick={axisTick(tokens)} stroke={tokens.border} allowDecimals={false} />
        <YAxis type="category" dataKey="topic" width={150} tick={axisTick(tokens)} stroke={tokens.border} />
        <Tooltip {...tooltipProps(tokens)} />
        <Bar dataKey="count" name="Conversations" fill={tokens.ring} radius={[0, 4, 4, 0]} maxBarSize={22} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  )
}


/**
 * Cost per 1,000 tokens, by model.
 *
 * A table rather than a chart on purpose: this is a ranking of four or five
 * numbers that get compared to each other directly, and a bar chart adds a
 * decoding step without adding information. The number is the point.
 */
function CostPer1kTable({ tokens, data }: { tokens: ThemeTokens; data: CostPer1k[] }) {
  if (data.length === 0) {
    return <p style={{ color: tokens.textSecondary, fontSize: '0.86rem' }}>No token usage in this window.</p>
  }
  const rows = [...data].sort((a, b) => Number(b.cost_per_1k ?? 0) - Number(a.cost_per_1k ?? 0))
  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%', minWidth: 0 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
        <thead>
          <tr>
            {['Model', '$ / 1k tokens', 'Tokens', 'Spend'].map((header, i) => (
              <th
                key={header}
                style={{
                  textAlign: i === 0 ? 'left' : 'right',
                  padding: '0.4rem 0.5rem',
                  borderBottom: `1px solid ${tokens.border}`,
                  color: tokens.textSecondary,
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.model}>
              <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${tokens.border}`, color: tokens.textPrimary }}>
                {row.model}
              </td>
              <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${tokens.border}`, textAlign: 'right', color: tokens.textPrimary, ...TABULAR }}>
                ${Number(row.cost_per_1k ?? 0).toFixed(4)}
              </td>
              <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${tokens.border}`, textAlign: 'right', color: tokens.textSecondary, ...TABULAR }}>
                {Number(row.tokens ?? 0).toLocaleString('en-AU')}
              </td>
              <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${tokens.border}`, textAlign: 'right', color: tokens.textSecondary, ...TABULAR }}>
                ${Number(row.spend ?? 0).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Department coverage. Three bars per department on one shared scale, so the
 * GAP between them is the thing the eye lands on — a team with fifteen
 * onboarded and two ever-active is a rollout that stalled, and that shape is
 * legible without reading a single number.
 */
function CoverageTable({
  tokens,
  data,
  days,
}: {
  tokens: ThemeTokens
  data: DepartmentCoverage[]
  days: number
}) {
  if (data.length === 0) {
    return <p style={{ color: tokens.textSecondary, fontSize: '0.86rem' }}>No directory data available.</p>
  }
  const max = Math.max(1, ...data.map((row) => Number(row.onboarded ?? 0)))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
      {data.map((row) => {
        const onboarded = Number(row.onboarded ?? 0)
        const ever = Number(row.ever_active ?? 0)
        const recent = Number(row.active_in_window ?? 0)
        const stalled = onboarded > 0 && ever / onboarded < 0.5
        return (
          <div key={row.department}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
              <span style={{ color: tokens.textPrimary, fontWeight: 600 }}>
                {row.department}
                {stalled ? <span style={{ color: tokens.danger }}> · stalled</span> : null}
              </span>
              <span style={{ color: tokens.textSecondary, ...TABULAR }}>
                {onboarded} onboarded · {ever} ever · {recent} in {days}d
              </span>
            </div>
            <div style={{ position: 'relative', height: '0.55rem', marginTop: '0.2rem' }}>
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: `${(onboarded / max) * 100}%`,
                  background: tokens.surfaceRaised,
                  border: `1px solid ${tokens.border}`,
                  borderRadius: '9999px',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: `${(ever / max) * 100}%`,
                  background: tokens.ring,
                  borderRadius: '9999px',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: `${(recent / max) * 100}%`,
                  background: tokens.brand,
                  borderRadius: '9999px',
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Topic concentration: one number, said plainly. */
function ConcentrationPanel({ tokens, share }: { tokens: ThemeTokens; share: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: '0.4rem' }}>
      <div style={{ fontSize: '2.6rem', fontWeight: 700, color: tokens.textPrimary, ...TABULAR }}>
        {share.toFixed(0)}%
      </div>
      <div style={{ fontSize: '0.85rem', color: tokens.textSecondary, lineHeight: 1.5 }}>
        of all classified titles fall in the top three topics. A board that is almost entirely one topic is a
        different platform from one spread evenly across the taxonomy.
      </div>
    </div>
  )
}

/** A generic two-key breakdown table, shared by topic x department and topic x model. */
function PairTable({
  tokens,
  rows,
  leftHeader,
  rightHeader,
}: {
  tokens: ThemeTokens
  rows: Array<{ left: string; right: string; count: number | string }>
  leftHeader: string
  rightHeader: string
}) {
  if (rows.length === 0) {
    return <p style={{ color: tokens.textSecondary, fontSize: '0.86rem' }}>No classified titles in this window.</p>
  }
  return (
    <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '18rem', maxWidth: '100%', minWidth: 0 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
        <thead>
          <tr>
            {[leftHeader, rightHeader, 'Count'].map((header, i) => (
              <th
                key={header}
                style={{
                  textAlign: i === 2 ? 'right' : 'left',
                  padding: '0.35rem 0.5rem',
                  borderBottom: `1px solid ${tokens.border}`,
                  color: tokens.textSecondary,
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 40).map((row) => (
            <tr key={`${row.left}:${row.right}`}>
              <td style={{ padding: '0.3rem 0.5rem', borderBottom: `1px solid ${tokens.border}`, color: tokens.textPrimary }}>
                {row.left}
              </td>
              <td style={{ padding: '0.3rem 0.5rem', borderBottom: `1px solid ${tokens.border}`, color: tokens.textSecondary }}>
                {row.right}
              </td>
              <td style={{ padding: '0.3rem 0.5rem', borderBottom: `1px solid ${tokens.border}`, textAlign: 'right', color: tokens.textPrimary, ...TABULAR }}>
                {Number(row.count ?? 0).toLocaleString('en-AU')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function DashboardsPage({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])

  const [days, setDays] = useState<DayRange>(30)
  const [tab, setTab] = useState<TabKey>('cost')

  const [cost, setCost] = useState<CostData | null>(null)
  const [usage, setUsage] = useState<UsageData | null>(null)
  const [adoption, setAdoption] = useState<AdoptionData | null>(null)
  const [topics, setTopics] = useState<TopicsData | null>(null)
  const [budget, setBudget] = useState<BudgetData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (range: DayRange) => {
    setLoading(true)
    setError(null)
    try {
      const [costData, usageData, adoptionData, topicsData, budgetData] = await Promise.all([
        apiJson<CostData>(`/api/dash/cost?days=${range}`),
        apiJson<UsageData>(`/api/dash/usage?days=${range}`),
        apiJson<AdoptionData>(`/api/dash/adoption?days=${range}`),
        apiJson<TopicsData>(`/api/dash/topics?days=${range}`),
        apiJson<BudgetData>(`/api/dash/budget?days=${range}`),
      ])
      setCost(costData)
      setUsage(usageData)
      setAdoption(adoptionData)
      setTopics(topicsData)
      setBudget(budgetData)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(days)
  }, [load, days])

  const tabListStyle: CSSProperties = { display: 'flex', gap: '0.4rem' }
  const tabButtonStyle = (active: boolean): CSSProperties => ({
    padding: '0.5rem 1.1rem',
    borderRadius: '9999px',
    border: `1.5px solid ${active ? tokens.brand : tokens.border}`,
    background: active ? tokens.brand : 'transparent',
    color: active ? tokens.brandContrast : tokens.textPrimary,
    fontWeight: 600,
    fontSize: '0.85rem',
    cursor: 'pointer',
  })
  const dayButtonStyle = (active: boolean): CSSProperties => ({
    ...styles.buttonGhost,
    borderColor: active ? tokens.brand : tokens.border,
    color: active ? tokens.brand : tokens.textPrimary,
    ...TABULAR,
  })
  const chartRow: CSSProperties = { display: 'flex', gap: '1.2rem', flexWrap: 'wrap' }

  return (
    <div style={styles.page}>
      <div style={styles.toolbar}>
        <div role="tablist" aria-label="Dashboard section" style={tabListStyle}>
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              style={tabButtonStyle(tab === t.key)}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <span style={styles.hint}>Window</span>
        {DAY_OPTIONS.map((d) => (
          <button key={d} type="button" style={dayButtonStyle(days === d)} onClick={() => setDays(d)} disabled={loading}>
            {d}
          </button>
        ))}
      </div>

      {error ? <p style={styles.error}>{error}</p> : null}
      {loading && !cost && !usage && !adoption && !topics && !budget ? (
        <p style={styles.hint}>Loading dashboards…</p>
      ) : null}

      {tab === 'cost' ? (
        <div style={chartRow}>
          <ChartCard title="Spend by user" subtitle={`Top spenders over the last ${days} days.`} tokens={tokens}>
            <SpendByUserChart tokens={tokens} data={cost?.byUser ?? []} />
          </ChartCard>
          <ChartCard title="Spend by model" subtitle={`Gateway spend per model, last ${days} days.`} tokens={tokens}>
            <SpendByModelChart tokens={tokens} data={cost?.byModel ?? []} />
          </ChartCard>
          <ChartCard
            title="Daily spend trend"
            subtitle="Platform-wide spend per day, most recent day emphasized."
            tokens={tokens}
            height="16rem"
          >
            <DailySpendTrendChart tokens={tokens} data={cost?.daily ?? []} />
          </ChartCard>
          {/* The chart that earns its place: it is the only one that can
              tell you a model swap would save money, and every input was
              already in the table. */}
          <ChartCard
            title="Cost per 1,000 tokens"
            subtitle={`Spend divided by tokens, per model, last ${days} days. Source: LiteLLM_SpendLogs.`}
            tokens={tokens}
          >
            <CostPer1kTable tokens={tokens} data={cost?.costPer1k ?? []} />
          </ChartCard>
        </div>
      ) : null}

      {tab === 'usage' ? (
        <div style={chartRow}>
          <ChartCard title="Active users" subtitle="Distinct daily active users, new users in teal." tokens={tokens}>
            <ActiveUsersTrendChart tokens={tokens} data={usage?.activeUsersDaily ?? []} />
          </ChartCard>
          <ChartCard title="Tokens per day" subtitle="Input + output tokens across the gateway." tokens={tokens}>
            <TokensPerDayChart tokens={tokens} data={usage?.tokensDaily ?? []} />
          </ChartCard>
          <ChartCard title="Feature mix" subtitle="Where usage splits across features and tools." tokens={tokens}>
            <FeatureMixChart tokens={tokens} data={usage?.featureMix ?? []} />
          </ChartCard>
        </div>
      ) : null}

      {tab === 'adoption' ? (
        <div style={chartRow}>
          <ChartCard title="New users" subtitle={`First-seen users per day, last ${days} days.`} tokens={tokens}>
            <NewUsersChart tokens={tokens} data={adoption?.newUsers ?? []} />
          </ChartCard>
          <ChartCard title="By department" subtitle="Distinct users active in this window, by department." tokens={tokens}>
            <ByDepartmentChart tokens={tokens} data={adoption?.byDepartment ?? []} />
          </ChartCard>
          <ChartCard title="Top topics" subtitle="Conversation topics by volume (titles only, no content)." tokens={tokens}>
            <TopicsChart tokens={tokens} data={adoption?.topicTrend ?? []} />
          </ChartCard>
          {/* The gap between onboarded and ever-active IS the finding: it
              answers "which team did we onboard and then never hear from",
              which is the actual adoption question. */}
          <ChartCard
            title="Department coverage"
            subtitle="Onboarded vs ever active vs active in this window. The gap is where onboarding stalled."
            tokens={tokens}
          >
            <CoverageTable tokens={tokens} data={adoption?.coverage ?? []} days={days} />
          </ChartCard>
        </div>
      ) : null}

      {tab === 'topics' ? (
        <>
          {/* The standing privacy line, at the top of the tab, every time. */}
          <div style={{ ...styles.callout, marginBottom: '1rem' }} role="note">
            <strong>Titles only.</strong>{' '}
            {topics?.privacyNote ??
              'Conversation titles are classified in flight. No title text is stored, store_prompts is off, and no message content exists anywhere on the platform.'}
          </div>
          <div style={chartRow}>
            <ChartCard
              title="Topic mix over time"
              subtitle={`Topic volume per day, last ${days} days. Source: daily_topics.`}
              tokens={tokens}
            >
              <TopicsChart tokens={tokens} data={topics?.mix ?? []} />
            </ChartCard>
            <ChartCard
              title="Topic concentration"
              subtitle="Share of all classified titles held by the top three topics."
              tokens={tokens}
              height="16rem"
            >
              <ConcentrationPanel tokens={tokens} share={topics?.top3Share ?? 0} />
            </ChartCard>
            <ChartCard
              title="Topic by department"
              subtitle="Which teams ask about what. Source: daily_topic_detail + user_directory."
              tokens={tokens}
            >
              <PairTable
                tokens={tokens}
                rows={(topics?.byDepartment ?? []).map((r) => ({
                  left: r.department,
                  right: r.topic,
                  count: r.count,
                }))}
                leftHeader="Department"
                rightHeader="Topic"
              />
            </ChartCard>
            <ChartCard
              title="Topic by model"
              subtitle="Which model each topic is being asked of. Source: daily_topic_detail."
              tokens={tokens}
            >
              <PairTable
                tokens={tokens}
                rows={(topics?.byModel ?? []).map((r) => ({ left: r.topic, right: r.model, count: r.count }))}
                leftHeader="Topic"
                rightHeader="Model"
              />
            </ChartCard>
          </div>
        </>
      ) : null}

      {tab === 'budget' ? (
        <div style={chartRow}>
          <ChartCard
            title="Burn over time"
            subtitle={`Daily spend, chat and developer, last ${days} days. Source: LiteLLM_SpendLogs.`}
            tokens={tokens}
            height="16rem"
          >
            <BurnOverTimeChart tokens={tokens} data={budget?.burn ?? []} />
          </ChartCard>
          <ChartCard
            title="Month to date"
            subtitle="This Sydney calendar month, projected to month end by a straight line."
            tokens={tokens}
          >
            {budget ? <MonthToDatePanel tokens={tokens} budget={budget} /> : <EmptyChart tokens={tokens} label="Loading." />}
          </ChartCard>
          <ChartCard
            title={budget?.hideCosts ? 'Top movers (tokens)' : 'Top movers (spend)'}
            subtitle="Biggest week-over-week change, this week against the prior week."
            tokens={tokens}
          >
            <TopMoversTable tokens={tokens} data={budget?.topMovers ?? []} />
          </ChartCard>
          <ChartCard
            title="Spend by department"
            subtitle={`Last ${days} days. Source: daily_user_model_spend + user_directory (nightly warehouse).`}
            tokens={tokens}
          >
            <SpendByDepartmentChart tokens={tokens} data={budget?.byDepartment ?? []} />
          </ChartCard>
        </div>
      ) : null}
    </div>
  )
}

export default DashboardsPage
